<template>
  <v-container
    v-if="courses"
    class="px-0 pt-0"
    fluid
  >
    <PageHero
      image="https://res.cloudinary.com/bridgecare/image/upload/v1628181324/media/Hands_-_Show_1_egfqly.svg"
      subtitle="Explore courses, complete assigned courses, and track your progress."
      title="Learning"
    />

    <v-container class="mxw-1200">
      <v-row class="d-flex align-center">
        <v-col
          cols="4"
          sm="4"
        >
          <v-tabs
            v-model="tab"
            bg-color="transparent"
          >
            <v-tab class="ls-normal tt-none fs-16 fw-500">
              {{ $bct('All') }}
            </v-tab>

            <v-tab class="ls-normal tt-none fs-16 fw-500">
              {{ $bct('In progress') }}
            </v-tab>

            <v-tab class="ls-normal tt-none fs-16 fw-500">
              {{ $bct('Completed') }}
            </v-tab>
          </v-tabs>
        </v-col>

        <v-col
          class="ta-right"
          cols="3"
          sm="3"
        >
          <!-- eslint-disable-next-line prefer-standard-components/no-vuetify-inputs -- TODO migrate to LabeledSelect -->
          <v-select
            v-model="selectedLabels"
            :item-title="(label) => $bct(label.name)"
            :items="courseLabels"
            :label="$bct('Tags')"
            class="ma-0 pa-0"
            color="primary"
            density="compact"
            item-value="id"
            variant="outlined"
            chips
            clearable
            hide-details
            multiple
            rounded
          />
        </v-col>

        <v-col
          class="ta-center"
          cols="2"
          sm="2"
        >
          <v-btn
            @click="$refs.invitationCodeDialog.open()"
            color="primary"
            prepend-icon="key"
            variant="flat"
            border
            tracked
          >
            {{ $bct('Enter code') }}
          </v-btn>
        </v-col>

        <v-col
          class="ta-right"
          cols="3"
          sm="3"
        >
          <!-- eslint-disable-next-line prefer-standard-components/no-vuetify-inputs -- TODO migrate to LabeledTextfield -->
          <v-text-field
            v-model="query"
            :aria-label="$bct('Search courses')"
            :placeholder="$bct('Search courses')"
            bg-color="white"
            density="compact"
            prepend-inner-icon="search"
            variant="outlined"
            flat
            hide-details
            rounded
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col
          v-for="course in queriedCourses"
          :key="course.id"
          class="d-grid align-end"
          cols="12"
          md="6"
        >
          <CourseCard
            :course="course"
            :course-completion="
              courseCompletions.filter(
                (courseCompletion) => courseCompletion.course_id == course.id,
              )[0]
            "
          />
        </v-col>
      </v-row>

      <VerticalSpacer />
    </v-container>

    <v-row
      v-if="courses.length === 0"
      class="justify-center px-2"
    >
      <v-col cols="3">
        <v-img
          :src="$a.url('icons.nullstate')"
          alt=""
        />
      </v-col>
    </v-row>

    <v-pagination
      v-show="pages > 1"
      v-model="page"
      :length="pages"
      :total-visible="8"
      class="mt-4"
    />

    <ResourceDialog
      @save="searchInvitationCode"
      ref="invitationCodeDialog"
      :fields="[
        {
          value: 'cohortInvitationCode',
          required: true,
        },
      ]"
      save-button-text="Search"
      title="Course code"
      closeable
    >
      <template #description>
        <p class="mb-4">
          {{ $bct('Open a course by entering its code below.') }}
        </p>
      </template>

      <template #append>
        <v-card
          class="mt-6"
          color="#D2DEE1"
          border
        >
          <v-card-title>
            {{ $bct("Don't have a code?") }}
          </v-card-title>

          <v-card-text>
            {{
              $bct("If you don't know your course's code, please contact your course facilitator.")
            }}
          </v-card-text>
        </v-card>
      </template>
    </ResourceDialog>
  </v-container>
</template>

<script setup>
import _ from 'lodash';
import { useRouter } from 'vue-router';

import Api from '@/manager/services/bright_finder';
import CourseCard from '@/shared/components/learning/CourseCard.vue';
import { newUtcDate } from '@/shared/services/date-helper';
import PageHero from '@/shared/components/content/PageHero.vue';
import ResourceDialog from '@/shared/components/form/ResourceDialog.vue';
import useEventBus from '@/shared/composables/useEventBus';
import VerticalSpacer from '@/shared/components/VerticalSpacer.vue';

const $bct = inject('translator');

const courseCompletions = ref([]);
const courseLabels = ref([]);
const courses = ref([]);
const eventBus = useEventBus();
const page = ref(1);
const pages = ref(1);
const query = ref('');
const router = useRouter();
const selectedLabels = ref([]);
const tab = ref(0);

const filteredCourses = computed(() => {
  if (tab.value === 0) return courses.value;

  if (tab.value === 1) {
    return courses.value.filter((course) => {
      const courseCompletion = courseCompletions.value.find(
        (courseCompletion) => courseCompletion.course_id === course.id,
      );
      return courseCompletion && courseCompletion.progress < 100;
    });
  }

  if (tab.value === 2) {
    return courses.value.filter((course) => {
      const courseCompletion = courseCompletions.value.find(
        (courseCompletion) => courseCompletion.course_id === course.id,
      );
      return courseCompletion && courseCompletion.progress === 100;
    });
  }

  return null;
});

const queriedCourses = computed(() => {
  if (!query.value && !selectedLabels.value) {
    return filteredCourses.value;
  }

  const queryLower = query.value.toLowerCase();

  return filteredCourses.value.filter((course) => {
    const labelIds = (course.labels ?? []).map((item) => item.id);
    const matchesQuery = course.name.toLowerCase().includes(queryLower);
    const matchesLabels =
      !selectedLabels.value.length ||
      selectedLabels.value.some((label) => labelIds.includes(label));

    return matchesQuery && matchesLabels;
  });
});

async function searchInvitationCode(query) {
  const filters = {
    filter: {
      invitation_code: query.cohortInvitationCode,
    },
  };

  const cohortResponse = await Api.cohort.index(filters);
  const cohort = cohortResponse?.data?.data[0];

  if (!cohort) {
    eventBus.chime('Code not found');
    return;
  } else if (!cohort.attributes?.start_date || !cohort.attributes?.end_date) {
    eventBus.chime('Code is not active');
    return;
  } else if (newUtcDate(cohort.attributes.end_date) < new Date()) {
    eventBus.chime('Code has expired');
    return;
  }

  const params = {
    data: {
      type: 'cohort_member',
      attributes: {},
      relationships: {
        cohort: {
          data: {
            type: 'cohort',
            id: cohort.id,
          },
        },
      },
    },
  };

  const response = await Api.cohortMember.create(params).catch((error) => {
    eventBus.chime(error.response.data.errors[0]);
  });

  if (response?.status !== 201) return;

  eventBus.chime('Enrolled in cohort');

  const { data } = await Api.cohort.get(cohort.id);

  const courseId = data?.data?.relationships?.course?.data?.id;
  if (!courseId) {
    eventBus.chime('Course not found');
    return;
  }

  await router.push({ name: 'CourseShow', params: { courseId } });
}

async function load() {
  await loadCourses();
  void loadCoursesFromCohorts();
  void loadCourseCompletion();
}

async function loadCourseCompletion() {
  const response = await Api.manager.courseCompletion.index();
  courseCompletions.value = response.data;
}

async function loadCourses() {
  const response = await Api.publicApi.organization.course.index();
  courses.value = response.data;
  courseLabels.value = _(courses.value).flatMap('labels').uniqBy('id').value();
}

async function loadCoursesFromCohorts() {
  const cohortMembersResponse = await Api.cohortMember
    .index({ include: 'cohort' })
    .catch((error) => {
      eventBus.chime(error.response.data.errors[0]);
    });

  const cohortMembers = cohortMembersResponse?.data?.data || [];

  await Promise.all(
    cohortMembers.map(async (cohortMember) => {
      const cohortId = cohortMember.relationships?.cohort?.data?.id;
      if (!cohortId) return;

      const cohortResponse = await Api.cohort.get(cohortId, { include: 'course,labels' });
      const coursesResponse =
        cohortResponse?.data?.included.filter((item) => item.type === 'course') || [];
      if (!coursesResponse) return;

      const course = coursesResponse[0];

      if (course.attributes?.published) {
        const labelsV2Format =
          cohortResponse?.data?.included.filter((item) => item.type === 'label') || [];
        const labels = labelsV2Format.map((label) => {
          return {
            id: label.id,
            ...label.attributes,
          };
        });

        if (labels.length) {
          courseLabels.value = _(courseLabels.value).concat(labels).uniqBy('id').value();
        }

        // The other course objects are not in a V2 format
        courses.value.push({
          id: course.id,
          labels,
          ...course.attributes,
        });
      }
    }),
  );
}

onMounted(load);
</script>
